import { useState, useEffect } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { getImageUrl } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { applyTenantTheme } from "@/lib/theme";
import { normalizeNumberInput, sanitizeLeadingZeroInput } from "@/lib/utils";

const DEFAULT_HERO_IMG = "/hero-preview-20260512.jpg";
const DEFAULT_HERO_MOBILE_IMG = "/hero-mobile-preview-20260512.jpg";
const resolveHeroImageUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("/") || path.startsWith("http") || path.startsWith("data:")) return path;
  return getImageUrl(path);
};

export default function AdminSettings() {
  const [form, setForm] = useState({ store_name: "", logo_url: "", primary_color: "#8A9A86", primary_foreground_color: "#FFFFFF", secondary_color: "#D5CABD", description: "", hero_image_url: DEFAULT_HERO_IMG, hero_image_mobile_url: DEFAULT_HERO_MOBILE_IMG, hero_overlay_opacity: 35, payment_timeout_rs: 24, payment_timeout_po: 72 });
  const [loading, setLoading] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [uploadingHeroMobile, setUploadingHeroMobile] = useState(false);
  const { t, locale } = useLanguage();

  useEffect(() => {
    API.get("/tenant").then((r) => {
      const tenant = r.data || {};
      setForm((f) => ({
        ...f,
        ...tenant,
        hero_image_url: tenant.hero_image_url || DEFAULT_HERO_IMG,
        hero_image_mobile_url: tenant.hero_image_mobile_url || tenant.hero_image_url || DEFAULT_HERO_MOBILE_IMG,
        hero_overlay_opacity: Math.max(0, Math.min(100, Number(tenant.hero_overlay_opacity ?? 35))),
      }));
    }).catch(() => {});
  }, []);

  const uploadHeroImage = async (file, field, setUploading) => {
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      data.append("scope", field === "hero_image_mobile_url" ? "landing-hero-mobile" : "landing-hero");
      const res = await API.post("/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, [field]: res.data?.url || res.data?.path || "" }));
      toast.success("Hero image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to upload hero image");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        hero_overlay_opacity: Math.max(0, Math.min(100, Number(form.hero_overlay_opacity ?? 35))),
      };
      await API.put("/tenant", payload);
      applyTenantTheme(payload);
      toast.success(t("common.saved") || "Settings updated!");
    } catch { toast.error("Failed to update"); }
    finally { setLoading(false); }
  };

  return (
    <AdminLayout title={t("admin_nav.settings")}>
      <div className="max-w-xl" data-testid="admin-settings">
        <div className="bg-white border border-stone-100 p-6 space-y-6">
          <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">{t("admin_settings.config_title")}</p>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_settings.store_name")}</Label>
            <Input value={form.store_name} onChange={(e) => setForm({ ...form, store_name: e.target.value })} className="mt-1" data-testid="tenant-name-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">Logo URL</Label>
            <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="mt-1" />
          </div>
          <div className="border-t border-stone-100 pt-6 space-y-4">
            <div>
              <p className="font-sans text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">Hero Landing Page</p>
              <p className="text-xs text-stone-500 mt-1">Atur gambar hero desktop, mobile, dan opacity overlay.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Hero Image Desktop</Label>
                {form.hero_image_url && (
                  <img src={resolveHeroImageUrl(form.hero_image_url)} alt="Hero desktop preview" className="mt-2 h-28 w-full object-cover rounded-md border border-stone-100" />
                )}
                <Input value={form.hero_image_url || ""} onChange={(e) => setForm({ ...form, hero_image_url: e.target.value })} className="mt-2" placeholder="Hero image URL" />
                <Input type="file" accept="image/*" disabled={uploadingHero} onChange={(e) => uploadHeroImage(e.target.files?.[0], "hero_image_url", setUploadingHero)} className="mt-2" />
                <p className="text-xs text-stone-400 mt-1">{uploadingHero ? "Uploading..." : "Upload gambar horizontal untuk desktop/tablet."}</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Hero Image Mobile</Label>
                {form.hero_image_mobile_url && (
                  <img src={resolveHeroImageUrl(form.hero_image_mobile_url)} alt="Hero mobile preview" className="mt-2 h-28 w-full object-cover rounded-md border border-stone-100" />
                )}
                <Input value={form.hero_image_mobile_url || ""} onChange={(e) => setForm({ ...form, hero_image_mobile_url: e.target.value })} className="mt-2" placeholder="Hero mobile image URL" />
                <Input type="file" accept="image/*" disabled={uploadingHeroMobile} onChange={(e) => uploadHeroImage(e.target.files?.[0], "hero_image_mobile_url", setUploadingHeroMobile)} className="mt-2" />
                <p className="text-xs text-stone-400 mt-1">{uploadingHeroMobile ? "Uploading..." : "Upload gambar portrait untuk mobile."}</p>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">Overlay Opacity: {form.hero_overlay_opacity ?? 35}%</Label>
              <Input type="range" min="0" max="100" step="5" value={form.hero_overlay_opacity ?? 35} onChange={(e) => setForm({ ...form, hero_overlay_opacity: Number(e.target.value) })} className="mt-2" />
              <div className="flex items-center gap-3 mt-2">
                <Input type="number" min="0" max="100" value={form.hero_overlay_opacity ?? 35} onChange={(e) => setForm({ ...form, hero_overlay_opacity: Math.max(0, Math.min(100, Number(e.target.value || 0))) })} className="w-28" />
                <span className="text-xs text-stone-500">0 = tanpa overlay, 35 = tipis, 100 = hitam penuh.</span>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_settings.primary_color")}</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={form.primary_color || "#8A9A86"} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-10 h-10 border-0 cursor-pointer" />
                <Input value={form.primary_color || ""} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="flex-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">Text Primary</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={form.primary_foreground_color || "#FFFFFF"} onChange={(e) => setForm({ ...form, primary_foreground_color: e.target.value })} className="w-10 h-10 border-0 cursor-pointer" />
                <Input value={form.primary_foreground_color || ""} onChange={(e) => setForm({ ...form, primary_foreground_color: e.target.value })} className="flex-1" placeholder="#FFFFFF" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_settings.secondary_color")}</Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="color" value={form.secondary_color || "#D5CABD"} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="w-10 h-10 border-0 cursor-pointer" />
                <Input value={form.secondary_color || ""} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })} className="flex-1" />
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-stone-100 bg-stone-50 p-3">
            <p className="text-[11px] uppercase tracking-wider text-stone-400 font-semibold mb-2">Preview warna tombol</p>
            <div className="inline-flex items-center px-5 py-2 rounded-md text-sm font-semibold shadow-sm" style={{ backgroundColor: form.primary_color || "#8A9A86", color: form.primary_foreground_color || "#FFFFFF" }}>
              Contoh Tombol Primary
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500">{t("admin_settings.store_desc")}</Label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3}
              className="w-full mt-1 px-3 py-2 text-sm border border-stone-200 focus:outline-none focus:border-[#8A9A86]" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 border-t pt-4 block border-stone-100">{t("admin_settings.rs_timeout")}</Label>
              <Input type="number" value={form.payment_timeout_rs ?? ""} onChange={(e) => setForm({ ...form, payment_timeout_rs: sanitizeLeadingZeroInput(e.target.value) })} onBlur={(e) => setForm((prev) => ({ ...prev, payment_timeout_rs: normalizeNumberInput(e.target.value, 24) }))} className="mt-1" min={1} />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-stone-500 border-t pt-4 block border-stone-100">{t("admin_settings.po_timeout")}</Label>
              <Input type="number" value={form.payment_timeout_po ?? ""} onChange={(e) => setForm({ ...form, payment_timeout_po: sanitizeLeadingZeroInput(e.target.value) })} onBlur={(e) => setForm((prev) => ({ ...prev, payment_timeout_po: normalizeNumberInput(e.target.value, 72) }))} className="mt-1" min={1} />
            </div>
          </div>
          <button onClick={handleSave} disabled={loading}
            className="bg-primary text-primary-foreground px-6 py-3 text-sm uppercase tracking-wider hover:opacity-90 transition-colors disabled:opacity-50"
            data-testid="save-settings-btn"
          >
            {loading ? t("admin_settings.saving") : t("admin_settings.save_btn")}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
